import type { Collector, CollectorInput, CollectorUpdate } from '@detect/shared';
import { api, withTenant, type PaginatedResult } from './api';
import type { WithId } from './types';

/** Collector view: hashes never leave the API; claim codes appear ONCE
 *  in create/rotate responses. */
export type CollectorView = WithId<
  Omit<Collector, 'claimCodeHash' | 'tokenHash'>
>;

/** Create/rotate-claim response: the collector view plus the one-time
 *  claim code (detclaim_…) — shown once, never retrievable again. */
export type CollectorCreated = CollectorView & { claimCode: string };

export const collectorService = {
  list: (
    tenantId: string,
    params: { limit?: number; startAfter?: string } = {}
  ): Promise<PaginatedResult<CollectorView>> =>
    api.getPaginated<CollectorView>(withTenant('/collectors', tenantId, params)),

  get: (tenantId: string, id: string): Promise<CollectorView> =>
    api.get<CollectorView>(withTenant(`/collectors/${id}`, tenantId)),

  create: (tenantId: string, input: CollectorInput): Promise<CollectorCreated> =>
    api.post<CollectorCreated>(withTenant('/collectors', tenantId), input),

  rotateClaim: (tenantId: string, id: string): Promise<CollectorCreated> =>
    api.post<CollectorCreated>(
      withTenant(`/collectors/${id}/rotate-claim`, tenantId)
    ),

  /** Response carries the NEW collector token exactly once. */
  rotateToken: (
    tenantId: string,
    id: string
  ): Promise<CollectorView & { token: string }> =>
    api.post<CollectorView & { token: string }>(
      withTenant(`/collectors/${id}/rotate-token`, tenantId)
    ),

  update: (
    tenantId: string,
    id: string,
    patch: CollectorUpdate
  ): Promise<CollectorView> =>
    api.patch<CollectorView>(withTenant(`/collectors/${id}`, tenantId), patch),

  remove: (tenantId: string, id: string): Promise<void> =>
    api.delete(withTenant(`/collectors/${id}`, tenantId)),
};
